// How it works: what the Gita is, how chapters, lessons and verses fit
// together, what happens in a lesson, and where everything is. Shown once to
// newcomers on the way in (?start=1), and one tap away ever after.
import { html } from '../lib/html.js';
import { icon } from '../lib/icons.js';
import { getProgress, local, setLocal } from '../lib/store.js';
import { syncState } from '../lib/sync.js';
import { navigate } from '../lib/router.js';
import { getIndex, chapter, chapterSky, nextStop, lessons } from '../lib/content.js';
import { topbar } from '../components/chrome.js';

const TABS = [
  ['home', 'Today', 'Your next lesson, your streak and a verse for the day.'],
  ['journey', 'Journey', 'All 18 chapters. Open one to see its lessons, or read it straight through.'],
  ['explore', 'Explore', 'Key ideas such as dharma and karma, who is who, famous verses, and search.'],
  ['bookmark', 'Saved', 'The verses you bookmark and the notes you write.'],
  ['user', 'Settings', 'Sign in, text size, night mode, and showing the original Sanskrit.'],
];

const STEPS = [
  ['Set the scene', 'A short introduction: where we are in the story and what to look for.'],
  ['Read the verses', 'Each verse in plain English with what it means. The Sanskrit is one tap away.'],
  ['Quick check', 'Two or three questions, so the ideas stick.'],
  ['Reflect', 'One question to think about, with room for a private note.'],
];

// Chapter, lesson, verse, zooming in on a real example: verse 2.47.
function anatomy() {
  const ch2 = chapter(2);
  const lesson = ch2.lessons.find((l) => l.from <= 47 && l.to >= 47);
  const verses = [];
  for (let v = lesson.from; v <= lesson.to; v++) verses.push(v);
  return html`<div class="anatomy">
    <div class="anatomy__tier">
      <p class="anatomy__label"><span class="anatomy__count num">18</span> chapters</p>
      <ol class="anatomy__chapters" aria-hidden="true">
        ${getIndex().chapters.map((c) => html`<li class="anatomy__chapter${c.ch === 2 ? ' is-on' : ''}${chapterSky(c.ch).ink === '#fff8ec' ? ' is-dusk' : ''}" style="--c:${chapterSky(c.ch).top};--i:${chapterSky(c.ch).ink}">${c.ch}</li>`)}
      </ol>
      <p class="anatomy__text">The Gita's own parts. Chapter 1 opens with Arjuna's despair; chapter 18 ends with his clarity.</p>
    </div>
    <div class="anatomy__tier">
      <p class="anatomy__label"><span class="anatomy__count num">${lessons().length}</span> lessons <span class="anatomy__eg">Chapter 2 has ${ch2.lessons.length}</span></p>
      <p class="anatomy__text">A lesson is one sitting of about ten minutes: a few verses in a row, each explained in plain English, then a quick check. You take them in order. Chapter 2's ${ch2.verses} verses make these ${ch2.lessons.length}:</p>
      <ol class="anatomy__lesson-list">
        ${ch2.lessons.map((l) => html`<li class="anatomy__lrow${l.id === lesson.id ? ' is-on' : ''}">
          <span class="anatomy__ln num">${l.n}</span>
          <span class="anatomy__lt">${l.title}</span>
          <span class="anatomy__lv num">${l.from === l.to ? `Verse ${l.from}` : `Verses ${l.from} to ${l.to}`}</span>
        </li>`)}
      </ol>
      <a class="link anatomy__open" href="/chapter/2">Open chapter 2 ${icon('chevron-right', { size: 16 })}</a>
    </div>
    <div class="anatomy__tier">
      <p class="anatomy__label"><span class="anatomy__count num">700</span> verses <span class="anatomy__eg">Lesson ${lesson.n} has ${verses.length}</span></p>
      <ol class="anatomy__verses" aria-hidden="true">
        ${verses.map((v) => html`<li class="anatomy__verse${v === 47 ? ' is-on' : ''}">2.${v}</li>`)}
      </ol>
      <p class="anatomy__text">The numbered lines of the text: 2.47 means chapter 2, verse 47. Each lesson covers a handful of them, and you can open any verse on its own.</p>
    </div>
  </div>`;
}

export default async function guide(params, r) {
  const start = r.query && r.query.get('start') === '1';
  const signedIn = !!(syncState().user || local().user);
  const p = getProgress();
  const stop = nextStop(p);
  const begun = lessons().some((l) => p.lessons[l.id]);
  const startHref = signedIn ? '/lesson/1-1' : '/signin?why=start&next=%2Flesson%2F1-1';
  const cta = start || !begun
    ? { label: 'Start chapter 1', href: startHref }
    : { label: stop ? 'Continue your journey' : 'Back to Today', href: stop ? `/lesson/${stop.lesson.id}` : '/' };

  return {
    title: 'How it works',
    focus: start,
    html: html`<div class="guide${start ? ' guide--start' : ''}">
      ${start
        ? html`<header class="guide__bar"><button type="button" class="icon-btn" data-back="/welcome" aria-label="Back">${icon('arrow-left', { size: 22 })}</button></header>`
        : topbar({ back: '/', title: 'How it works' })}
      <header class="wrap page-head guide__head">
        <p class="eyebrow">How it works</p>
        <h1 class="h1">Your way through the Gita</h1>
        <p class="lede">What the Gita is, how this app is organised, and how to find your way. One minute to read.</p>
      </header>
      <div class="wrap guide__body">
        <section class="section guide__section" aria-labelledby="g-what">
          <h2 class="h2" id="g-what">What the Gita is</h2>
          <p class="guide__text">The Bhagavad Gita, the "Song of the Lord", is a 700-verse conversation inside India's great epic, the Mahabharata. On the morning of a war between two branches of one family, the warrior Arjuna sees his teachers and cousins in the opposing army and refuses to fight. His charioteer, Krishna, answers him. Their dialogue, about duty, action, the self and devotion, has guided people for two thousand years.</p>
        </section>

        <section class="section guide__section" aria-labelledby="g-shape">
          <h2 class="h2" id="g-shape">Chapters, lessons and verses</h2>
          <p class="guide__text">The Gita comes in chapters and verses. This app adds lessons, so you can take it a few verses at a time.</p>
          ${anatomy()}
        </section>

        <section class="section guide__section" aria-labelledby="g-lesson">
          <h2 class="h2" id="g-lesson">What happens in a lesson</h2>
          <ol class="guide__steps">
            ${STEPS.map(([title, text], i) => html`<li class="guide__step"><span class="guide__step-n num" aria-hidden="true">${i + 1}</span><span><span class="guide__step-title">${title}</span><span class="guide__step-text">${text}</span></span></li>`)}
          </ol>
          <p class="guide__text guide__after">About ten minutes, and each lesson ends with one thing to remember.</p>
        </section>

        <section class="section guide__section" aria-labelledby="g-nav">
          <h2 class="h2" id="g-nav">Finding your way</h2>
          <p class="guide__text">Five places, along the bottom on a phone and down the left on a computer.</p>
          <ul class="guide__tabs">
            ${TABS.map(([ic, title, text]) => html`<li class="guide__tab"><span class="guide__tab-icon" aria-hidden="true">${icon(ic, { size: 22 })}</span><span><span class="guide__tab-title">${title}</span><span class="guide__tab-text">${text}</span></span></li>`)}
          </ul>
        </section>

        <section class="section guide__section" aria-labelledby="g-progress">
          <h2 class="h2" id="g-progress">Your progress</h2>
          <p class="guide__text">Every lesson you finish is saved as you go. Sign in with your email and it is kept in your account, ready on any phone or computer. No password: we email you a 6-digit code.</p>
        </section>

        <section class="section guide__section guide__background" aria-labelledby="g-bg">
          <h2 class="h3" id="g-bg">New to the story?</h2>
          <p class="guide__text">Four short background lessons cover what the Gita is, the family feud behind the war, who is who, and how to read the conversation. They are optional; read them first or dip in any time.</p>
          <a class="btn btn--secondary btn--small guide__bg-link" href="/chapter/0">${icon('sunrise', { size: 16 })} Read the background</a>
        </section>

        ${start ? '' : html`<div class="section guide__end"><a class="btn btn--primary btn--block" href="${cta.href}">${cta.label} ${icon('arrow-right', { size: 20 })}</a></div>`}
      </div>
      ${start ? html`<footer class="guide__foot"><div class="guide__foot-inner"><a class="btn btn--primary btn--block" href="${cta.href}" data-start>${cta.label} ${icon('arrow-right', { size: 20 })}</a></div></footer>` : ''}
    </div>`,
    mount(root) {
      const go = root.querySelector('[data-start]');
      if (go) {
        go.addEventListener('click', (e) => {
          e.preventDefault();
          setLocal({ welcomed: true });
          navigate(cta.href);
        });
      }
    },
  };
}
